import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Copy, Headphones, Search, Sparkles, SquareTerminal, X } from "lucide-react";
import Logo from "@/components/Logo";
import { DOCS, DOC_SHORTCUTS, DOC_TABS } from "@/content/docs";
import Blocks from "./Renderer";
import CookieBanner from "./CookieBanner";
import "@/docs.css";

const SHORTCUT_ICONS = { Code2: SquareTerminal, LifeBuoy: Headphones };

function Palette({ onClose }) {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return DOCS.slice(0, 6);
    return DOCS.filter(d =>
      (d.title + " " + d.subtitle + " " + d.group + " " + JSON.stringify(d.sections)).toLowerCase().includes(needle)
    ).slice(0, 8);
  }, [q]);

  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="docs-palette-backdrop" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="docs-palette" role="dialog" aria-label="Rechercher dans la documentation">
        <div className="docs-palette-input">
          <Search size={16} color="#98a08d" />
          <input
            autoFocus value={q} onChange={e => setQ(e.target.value)} data-testid="docs-search-input"
            placeholder="Rechercher dans la documentation…"
            onKeyDown={e => { if (e.key === "Enter" && results[0]) { nav(`/docs/${results[0].slug}`.replace(/\/$/, "")); onClose(); } }}
          />
          <button type="button" onClick={onClose} aria-label="Fermer"><X size={15} color="#98a08d" /></button>
        </div>
        <div className="docs-palette-results">
          {results.length === 0
            ? <div className="docs-palette-empty">Aucun résultat pour « {q} ».</div>
            : results.map(d => (
                <Link key={d.slug} to={`/docs/${d.slug}`.replace(/\/$/, "")} onClick={onClose}>
                  <strong>{d.title}</strong><small>{d.breadcrumb} · {d.subtitle}</small>
                </Link>
              ))}
        </div>
      </div>
    </div>
  );
}

export default function DocsLayout({ doc }) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [copied, setCopied] = useState(false);
  const contentRef = useRef(null);

  const sameTab = useMemo(() => DOCS.filter(d => d.tab === doc.tab), [doc.tab]);
  const groups = useMemo(() => {
    const out = [];
    sameTab.forEach(d => {
      const g = out.find(x => x.title === d.group);
      (g ? g.items : (out.push({ title: d.group, items: [] }), out[out.length - 1].items)).push(d);
    });
    return out;
  }, [sameTab]);

  const index = sameTab.findIndex(d => d.slug === doc.slug);
  const prev = index > 0 ? sameTab[index - 1] : null;
  const next = index >= 0 && index < sameTab.length - 1 ? sameTab[index + 1] : null;
  const headings = doc.sections.filter(s => s.heading);

  useEffect(() => {
    document.title = `${doc.title} — Documentation Perform`;
    window.scrollTo(0, 0);
  }, [doc.slug, doc.title]);

  // Ctrl/Cmd+K ouvre la recherche, comme dans un éditeur.
  useEffect(() => {
    const onKey = e => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setPaletteOpen(true); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Met en évidence la section visible dans le sommaire.
  useEffect(() => {
    if (!headings.length) return;
    const observer = new IntersectionObserver(
      entries => {
        const visible = entries.filter(e => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveSection(visible[0].target.id);
      },
      { rootMargin: "-120px 0px -65% 0px" }
    );
    headings.forEach(h => { const el = document.getElementById(h.id); if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [doc.slug, headings]);

  const copyPage = useCallback(() => {
    const text = contentRef.current?.innerText || "";
    navigator.clipboard?.writeText(`# ${doc.title}\n\n${text}`).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1800);
    });
  }, [doc.title]);

  const href = slug => `/docs/${slug}`.replace(/\/$/, "");

  return (
    <div className="docs">
      <div className="docs-top">
        <div className="docs-top-row">
          <Link to="/" aria-label="Perform, accueil"><Logo /></Link>
          <div className="docs-tools">
            <button type="button" className="docs-search" onClick={() => setPaletteOpen(true)} data-testid="docs-search">
              <Search size={15} /><span>Rechercher…</span><kbd className="docs-kbd">⌘K</kbd>
            </button>
            <Link to="/aide" className="docs-ask" data-testid="docs-ask"><Sparkles size={14} />Besoin d'aide</Link>
          </div>
          <div className="docs-top-right">
            <Link to="/auth" className="button button-dark">Commencer</Link>
          </div>
        </div>
        <nav className="docs-tabs" aria-label="Sections de la documentation">
          {DOC_TABS.map(t => t.to
            ? <Link key={t.label} to={t.to}>{t.label}</Link>
            : <Link key={t.label} to={href(DOCS.find(d => d.tab === t.tab)?.slug ?? "")} className={t.tab === doc.tab ? "is-active" : undefined}>{t.label}</Link>)}
        </nav>
      </div>

      <div className="docs-body">
        <aside className="docs-side">
          {DOC_SHORTCUTS.map(s => {
            const Icon = SHORTCUT_ICONS[s.icon];
            return <Link key={s.to} to={s.to} className="docs-side-shortcut"><span>{Icon && <Icon size={14} />}</span>{s.label}</Link>;
          })}
          {groups.map(g => (
            <div key={g.title} className="docs-side-group">
              <h4>{g.title}</h4>
              {g.items.map(d => (
                <Link key={d.slug} to={href(d.slug)} className={d.slug === doc.slug ? "is-active" : undefined}>{d.nav}</Link>
              ))}
            </div>
          ))}
        </aside>

        <main className="docs-main">
          <div className="docs-breadcrumb">{doc.breadcrumb}</div>
          <div className="docs-title-row">
            <div>
              <h1>{doc.title}</h1>
              <p className="docs-subtitle">{doc.subtitle}</p>
            </div>
            <div className="docs-copy">
              <button type="button" onClick={copyPage} data-testid="docs-copy-page">
                <Copy size={13} />{copied ? "Copié" : "Copier la page"}
              </button>
              <button type="button" aria-label="Autres formats" onClick={copyPage}><ChevronRight size={13} /></button>
            </div>
          </div>

          <div className="docs-content" ref={contentRef}>
            {doc.sections.map(s => (
              <section key={s.id} id={s.id}>
                {s.heading && <h2>{s.heading}</h2>}
                <Blocks blocks={s.blocks} />
              </section>
            ))}
          </div>

          {(prev || next) && (
            <nav className="docs-nextprev" aria-label="Navigation entre pages">
              {prev ? <Link to={href(prev.slug)}><small>Précédent</small><strong>{prev.title}</strong></Link> : <span />}
              {next && <Link to={href(next.slug)} className="is-next"><small>Suivant</small><strong>{next.title}</strong></Link>}
            </nav>
          )}
        </main>

        <aside className="docs-toc">
          {headings.length > 0 && <>
            <h5>Sur cette page</h5>
            {headings.map(h => (
              <a key={h.id} href={`#${h.id}`} className={activeSection === h.id ? "is-active" : undefined}>{h.heading}</a>
            ))}
          </>}
        </aside>
      </div>

      {paletteOpen && <Palette onClose={() => setPaletteOpen(false)} />}
      <CookieBanner />
    </div>
  );
}
